import { useState } from "react";
import { nowMsk } from "../docs/helpers";
import { api } from "../api";
import type { Funding, OpDay, OpList } from "../types";
import { maskChange } from "../docs/mask";
import { useMessage } from "./Message";

const DATE_MASK = "99.99.9999";

export const FUNDINGS: { value: Funding; label: string; hint: string }[] = [
  { value: "oms", label: "ОМС РА", hint: "по полису, своя область" },
  { value: "mezhter", label: "МЕЖТЕР", hint: "межтерриториальный расчёт" },
  { value: "paid", label: "ПЛАТНО", hint: "за счёт пациента" },
  { value: "free", label: "БЕЗ ОПЛАТЫ", hint: "бесплатно — без расчёта" },
];

/** «oms+paid» → «ОМС РА+ПЛАТНО» — подпись составной оплаты дня. */
export function fundingLabel(funding: string | null | undefined): string {
  const codes = String(funding ?? "").split("+").filter(Boolean);
  const labels = codes
    .map((c) => FUNDINGS.find((f) => f.value === c)?.label)
    .filter(Boolean);
  return labels.length ? labels.join("+") : "ОМС РА";
}

/** «2026-09-15» → «15.09.2026», как дату пишут в клинике. */
function toRu(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
}

const DATE_RE = /^\d{2}\.\d{2}\.\d{4}$/;
const pad = (n: number) => String(n).padStart(2, "0");

function todayIso(): string {
  const d = nowMsk();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const MONTHS = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];
const DOW = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

/** Порядок, по которому клик крутит оплату даты: ОМС РА → МЕЖТЕР → ПЛАТНО → снять. */
const CYCLE: Funding[] = ["oms", "mezhter", "paid"];

/**
 * Мини-календарь массового открытия дней.
 *
 * Клик по дате крутит её оплату по кругу: первый — ОМС РА, второй — МЕЖТЕР,
 * третий — ПЛАТНО, четвёртый снимает выбор. Так неделя операционных дней
 * набирается десятком кликов, без ввода дат руками; цвет клетки — какая
 * оплата выбрана. Прошедшие даты не кликаются: задним числом день открывают
 * через поле даты, осознанно.
 */
function BulkCalendar({
  picked,
  onCycle,
}: {
  picked: Map<string, Funding>;
  onCycle: (iso: string) => void;
}) {
  const [shift, setShift] = useState(0);
  const base = nowMsk();
  base.setDate(1);
  base.setMonth(base.getMonth() + shift);
  const y = base.getFullYear();
  const m = base.getMonth();
  const firstDow = (base.getDay() + 6) % 7; // понедельник — первый
  const daysIn = new Date(y, m + 1, 0).getDate();
  const today = todayIso();
  const cells: (string | null)[] = Array.from({ length: firstDow }, () => null);
  for (let d = 1; d <= daysIn; d++) cells.push(`${y}-${pad(m + 1)}-${pad(d)}`);

  return (
    <div className="op-bulkcal">
      <div className="op-bulkcal-nav">
        <u onClick={() => setShift(shift - 1)}>‹</u>
        <b onClick={() => setShift(0)} title="К текущему месяцу">
          {MONTHS[m]} {y}
        </b>
        <u onClick={() => setShift(shift + 1)}>›</u>
      </div>
      <div className="op-bulkcal-grid">
        {DOW.map((d, i) => (
          <span key={d} className={`op-bulkcal-dow${i >= 5 ? " op-bulkcal-we" : ""}`}>
            {d}
          </span>
        ))}
        {cells.map((iso, i) =>
          iso ? (
            <span
              key={iso}
              className={
                `op-bulkcal-day${iso < today ? " op-bulkcal-past" : ""}` +
                `${iso === today ? " op-bulkcal-today" : ""}` +
                `${i % 7 >= 5 ? " op-bulkcal-we" : ""}` +
                `${picked.has(iso) ? ` op-bulkcal-${picked.get(iso)}` : ""}`
              }
              title={
                picked.has(iso)
                  ? `${toRu(iso)} — ${fundingLabel(picked.get(iso))}; клик — дальше по кругу`
                  : undefined
              }
              onClick={iso < today ? undefined : () => onCycle(iso)}
            >
              {Number(iso.slice(8))}
            </span>
          ) : (
            <span key={`e${i}`} />
          )
        )}
      </div>
      <div className="op-bulkcal-legend">
        клик: <em className="op-bulkcal-oms">ОМС РА</em> → <em className="op-bulkcal-mezhter">МЕЖТЕР</em> →{" "}
        <em className="op-bulkcal-paid">ПЛАТНО</em> → снять
      </div>
    </div>
  );
}

/**
 * «Открыть операционный день» — и правка уже открытого.
 *
 * День это не просто дата: у него есть вид оплаты (он определяет отчётность и
 * стоимость), плановое количество операций и комментарий. Оплата может быть
 * составной — день, где оперируют и по ОМС, и платных, — поэтому кнопки
 * работают как переключатели «один или несколько», а «другое» дописывает
 * словами то, для чего кода нет.
 *
 * Несколько дней сразу открываются календарём: даты выбираются кликами, и
 * каждая несёт свою оплату (цвет клетки). Пока в календаре есть выбранные
 * даты, поле даты и кнопки оплаты не участвуют — у каждого дня оплата уже
 * назначена кликами.
 *
 * План не запрещает записать сверх него: решение взять ещё одного пациента
 * остаётся за хирургом, а интерфейс лишь показывает заполненность. Пустые
 * места до плана видны в списке как свободные строки.
 */
export default function OpDayDialog({
  list,
  day,
  onClose,
  onSaved,
}: {
  list: OpList;
  /** null — открываем новый день; иначе правим существующий. */
  day: OpDay | null;
  onClose: () => void;
  onSaved: (dayId: number, text: string) => void;
}) {
  const message = useMessage();
  const [date, setDate] = useState(day ? toRu(day.op_date) : "");
  /** Массовое открытие: дата → её оплата, накликанные в календаре. */
  const [bulk, setBulk] = useState<Map<string, Funding>>(new Map());
  const [funding, setFunding] = useState<Set<Funding>>(() => {
    const codes = String(day?.funding ?? "oms").split("+").filter(Boolean) as Funding[];
    const known = codes.filter((c) => FUNDINGS.some((f) => f.value === c));
    return new Set(known.length ? known : ["oms"]);
  });
  const [fundingOther, setFundingOther] = useState(day?.funding_other ?? "");
  const [planned, setPlanned] = useState(String(day?.planned ?? ""));
  const [comment, setComment] = useState(day?.comment ?? "");
  const [folded, setFoldedFlag] = useState(!!day?.folded);
  const [busy, setBusy] = useState(false);

  const bulkMode = !day && bulk.size > 0;

  function cycleBulk(iso: string) {
    setBulk((prev) => {
      const next = new Map(prev);
      const cur = next.get(iso);
      if (cur === undefined) next.set(iso, CYCLE[0]);
      else {
        const at = CYCLE.indexOf(cur);
        if (at === CYCLE.length - 1) next.delete(iso);
        else next.set(iso, CYCLE[at + 1]);
      }
      return next;
    });
  }

  function toggleFunding(code: Funding) {
    setFunding((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        // Хотя бы один вид оплаты у дня есть всегда — снять последний нельзя.
        if (next.size > 1) next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }

  /** Коды в порядке кнопок, склеенные «+», — как их хранит сервер. */
  const fundingStr = FUNDINGS.filter((f) => funding.has(f.value))
    .map((f) => f.value)
    .join("+");
  const fundingText =
    FUNDINGS.filter((f) => funding.has(f.value)).map((f) => f.label).join("+") +
    (fundingOther.trim() ? `, ${fundingOther.trim()}` : "");

  async function save() {
    const shared = {
      funding_other: fundingOther.trim(),
      planned: Number(planned) || 0,
      comment,
      folded,
    };
    setBusy(true);
    try {
      if (day) {
        if (!DATE_RE.test(date)) {
          message("Укажите дату операционного дня", "error");
          setBusy(false);
          return;
        }
        await api.updateOpDay(day.day_id, { op_date: date, funding: fundingStr, ...shared });
        onSaved(day.day_id, "Операционный день изменён");
        return;
      }

      if (bulkMode) {
        // По дню на каждую накликанную дату, оплата — цвет её клетки.
        let firstId = 0;
        const errors: string[] = [];
        for (const [iso, code] of [...bulk].sort(([a], [b]) => (a < b ? -1 : 1))) {
          try {
            const res = await api.openOpDay({
              list_id: list.list_id,
              op_date: toRu(iso),
              funding: code,
              ...shared,
            });
            if (!firstId) firstId = res.day_id;
          } catch (err) {
            errors.push(`${toRu(iso)}: ${(err as Error).message}`);
          }
        }
        if (!firstId) {
          message(errors[0] ?? "Не получилось открыть дни", "error");
          setBusy(false);
          return;
        }
        const okText = `Открыто дней: ${bulk.size - errors.length}`;
        onSaved(firstId, errors.length ? `${okText}; не открылись — ${errors.join("; ")}` : okText);
        return;
      }

      if (!DATE_RE.test(date)) {
        message("Укажите дату — или выберите дни кликами в календаре", "error");
        setBusy(false);
        return;
      }
      const res = await api.openOpDay({
        list_id: list.list_id,
        op_date: date,
        funding: fundingStr,
        ...shared,
      });
      onSaved(res.day_id, `Открыт день ${res.label}`);
    } catch (err) {
      message(err as Error);
      setBusy(false);
    }
  }

  async function close() {
    if (!day) return;
    if (
      !confirm(
        `Закрыть день «${day.label}»?\n\nПациенты останутся в списке с этой датой — их можно перенести на другой день или вернуть, открыв день заново.`
      )
    )
      return;
    setBusy(true);
    try {
      await api.closeOpDay(day.day_id);
      onSaved(0, "Операционный день закрыт");
    } catch (err) {
      message(err as Error);
      setBusy(false);
    }
  }

  return (
    <div className="overlay overlay-op">
      <div className="overlay-frame op-day-frame">
        <div className="close" onClick={busy ? undefined : onClose} />
        <div className="overlay-header">
          {day ? "Операционный день" : "Открыть операционный день"}
          <span className="hist-sub">{list.name}</span>
        </div>

        <div className="op-form">
          <div className="op-form-row">
            <label>Дата</label>
            <input
              type="tel"
              className="op-date"
              placeholder="15.09.2026"
              disabled={bulkMode}
              value={date}
              onChange={(e) => setDate(maskChange(e, DATE_MASK, date))}
            />
            <span className="op-hint">Операций в плане</span>
            <input
              type="number"
              min={0}
              max={99}
              className="op-day-planned"
              placeholder="—"
              value={planned}
              onChange={(e) => setPlanned(e.target.value)}
            />
          </div>

          {!day && (
            <div className="op-form-row op-form-row-cal">
              <label>Несколько дней</label>
              <BulkCalendar picked={bulk} onCycle={cycleBulk} />
            </div>
          )}

          <div className="op-form-row">
            <label>Оплата</label>
            <div className={`op-funding${bulkMode ? " op-funding-off" : ""}`}>
              {FUNDINGS.map((f) => (
                <div
                  key={f.value}
                  className={`op-funding-pill${!bulkMode && funding.has(f.value) ? " active" : ""}`}
                  title={
                    bulkMode
                      ? "При выборе дней в календаре оплата назначается кликами по датам"
                      : `${f.hint} — можно выбрать несколько`
                  }
                  onClick={bulkMode ? undefined : () => toggleFunding(f.value)}
                >
                  {f.label}
                </div>
              ))}
              <input
                type="text"
                className="op-funding-other"
                placeholder="другое…"
                value={fundingOther}
                onChange={(e) => setFundingOther(e.target.value)}
              />
            </div>
          </div>

          <div className="op-form-row">
            <label>Комментарий</label>
            <input
              type="text"
              placeholder="например: две бригады, начало в 9:00"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <div className="op-form-row">
            <label />
            <label className="op-day-folded-opt" title="День в списке появится свёрнутым — его разворачивают по клику">
              <input
                type="checkbox"
                checked={folded}
                onChange={(e) => setFoldedFlag(e.target.checked)}
              />
              показывать день свёрнутым
            </label>
          </div>

          <div className="op-form-row">
            <label />
            <span className="op-hint">
              {bulkMode
                ? `Откроется ${bulk.size} ${bulk.size === 1 ? "день" : bulk.size < 5 ? "дня" : "дней"} — у каждого своя оплата, цветом в календаре`
                : `День появится в списке как «${date || "дата"} — ${fundingText}${comment.trim() ? ` — ${comment.trim()}` : ""}»`}
            </span>
          </div>

          <div className="op-form-buttons">
            <div className={`button green${busy ? " disabled" : ""}`} onClick={busy ? undefined : save}>
              {busy ? "…" : day ? "Сохранить" : bulkMode ? `Открыть дни (${bulk.size})` : "Открыть день"}
            </div>
            <div className="button" onClick={onClose}>
              Отмена
            </div>
            {day && (
              <div className="hist-remove" onClick={busy ? undefined : close}>
                Закрыть день
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
